import { useState } from 'react';
import { reopenPeriod } from '../../api/inventory';
import { periodLabel } from './ClosePeriodSelector';

interface Props {
  regionId:    string;
  regionName:  string;
  period:      string; // 'YYYY-MM'
  onClose:     () => void;
  onReopened:  () => void;
}

export default function ReopenCloseModal(props: Props) {
  const { regionId, regionName, period, onClose, onReopened } = props;
  const [reason, setReason] = useState('');
  const [saving, setSaving] = useState(false);
  const [error,  setError]  = useState<string | null>(null);

  async function handleReopen() {
    if (reason.trim().length === 0) {
      setError('A reason is required to re-open an approved period.');
      return;
    }
    setSaving(true);
    setError(null);
    try {
      await reopenPeriod(regionId, period, reason.trim());
      onReopened();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to re-open period.');
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
      <div className="bg-white dark:bg-[#1e2521] rounded-xl shadow-xl w-full max-w-md mx-4 p-6">
        <h2 className="text-lg font-semibold text-stone-900 dark:text-stone-100 mb-1">
          Re-open {periodLabel(period)} close
        </h2>
        <p className="text-sm text-stone-500 dark:text-stone-400 mb-4">
          {regionName}
        </p>

        <div className="mb-4 rounded-lg bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 px-3 py-2 text-sm text-amber-800 dark:text-amber-300">
          Re-opening an approved period unlocks it for new submissions and edits. The original approver cannot re-open their own approval — only another admin can.
        </div>

        <label className="block">
          <span className="text-sm font-medium text-stone-700 dark:text-stone-300">
            Reason <span className="text-red-500">*</span>
          </span>
          <textarea
            value={reason}
            onChange={e => setReason(e.target.value)}
            rows={3}
            placeholder="Why is this period being re-opened?"
            className="mt-1 block w-full rounded-lg border border-stone-300 dark:border-stone-600 bg-white dark:bg-[#242d28] px-3 py-2 text-sm text-stone-900 dark:text-stone-100 focus:outline-none focus:ring-2 focus:ring-amber-500 resize-none"
          />
        </label>

        {error && (
          <p className="mt-3 text-sm text-red-600 dark:text-red-400">{error}</p>
        )}

        <div className="mt-6 flex justify-end gap-3">
          <button
            onClick={onClose}
            disabled={saving}
            className="px-4 py-2 text-sm rounded-lg border border-stone-300 dark:border-stone-600 text-stone-700 dark:text-stone-300 hover:bg-stone-50 dark:hover:bg-[#242d28] transition"
          >
            Cancel
          </button>
          <button
            onClick={handleReopen}
            disabled={saving}
            className="px-4 py-2 text-sm rounded-lg bg-amber-600 text-white hover:bg-amber-700 transition disabled:opacity-50"
          >
            {saving ? 'Re-opening…' : 'Re-open close'}
          </button>
        </div>
      </div>
    </div>
  );
}
